import { Link } from "react-router-dom";
import { Clock, Trash2, X } from "lucide-react";
import { clearHistory, removeScan } from "@/lib/history";

function pillColor(score) {
  if (score >= 80) return "text-emerald-400 border-emerald-500/30 bg-emerald-500/10";
  if (score >= 50) return "text-amber-400 border-amber-500/30 bg-amber-500/10";
  return "text-red-400 border-red-500/30 bg-red-500/10";
}

export const ScanHistory = ({ history, onChange, compact = false }) => {
  if (!history?.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/50 p-6 text-center">
        <Clock className="mx-auto h-6 w-6 text-slate-600" />
        <p className="mt-2 text-sm text-slate-400">No scans yet. Run your first one above.</p>
      </div>
    );
  }

  const clearAll = () => {
    clearHistory();
    onChange?.([]);
  };

  const remove = (id) => {
    const next = removeScan(id);
    onChange?.(next);
  };

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5" data-testid="scan-history-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-cyan-400" />
          <h3 className="font-display font-semibold text-slate-100">Recent scans</h3>
          <span className="text-xs text-slate-500 font-mono">({history.length})</span>
        </div>
        <button
          type="button"
          onClick={clearAll}
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400 transition-colors"
          data-testid="clear-history-button"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Clear all
        </button>
      </div>
      <ul className="space-y-2" data-testid="scan-history-list">
        {history.slice(0, compact ? 5 : history.length).map((scan) => (
          <li
            key={scan.id}
            className="group flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2.5 hover:border-cyan-500/30 transition-colors"
            data-testid="scan-history-item"
          >
            <span className={`shrink-0 inline-flex min-w-[2.75rem] justify-center rounded-md border px-2 py-0.5 text-xs font-bold font-mono ${pillColor(scan.score)}`}>
              {scan.score}
            </span>
            <div className="flex-1 min-w-0">
              <Link
                to={`/dashboard?id=${scan.id}`}
                className="block truncate text-sm font-medium text-slate-200 hover:text-cyan-300"
                data-testid={`scan-history-link-${scan.id}`}
              >
                {scan.domain}
              </Link>
              <div className="text-[11px] text-slate-500 font-mono">
                {new Date(scan.scanned_at).toLocaleString()}
                {" · "}
                <span className="text-red-400/80">{scan.counts.critical}C</span>{" "}
                <span className="text-orange-400/80">{scan.counts.high}H</span>{" "}
                <span className="text-amber-400/80">{scan.counts.medium}M</span>{" "}
                <span className="text-emerald-400/80">{scan.counts.passed}P</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => remove(scan.id)}
              className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition"
              aria-label="Remove scan"
              data-testid={`remove-scan-${scan.id}`}
            >
              <X className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ScanHistory;
